import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonButton, IonIcon, IonSpinner, IonText } from '@ionic/react';
import { addOutline, chevronForwardOutline, qrCodeOutline, businessOutline, createOutline } from 'ionicons/icons';
import { useLots } from '../../application/use-cases/lots/useLots';
import { formatDate, formatGdd, type Lot } from '../../domain/Lot';
import { shortDate } from '../../domain/WeatherStation';
import { joinPathForScan } from '../../domain/DeviceShare';
import QrScannerModal from '../components/QrScannerModal';
import ProgressRing from '../components/lot/ProgressRing';
import './Lots.css';

// Cada lote con la foto del campo de portada (corrida para que no se vean
// todas iguales), su etapa y lo que falta para la siguiente.
const LotCard: React.FC<{ lot: Lot; index: number; onOpen: () => void }> = ({ lot, index, onOpen }) => {
  const status = lot.status;
  const ph = status?.phenology;
  const tracking = status && ph && lot.stations.length > 0;
  return (
    <button className="lot-card" onClick={onOpen}>
      <span className="lot-card-cover" style={{ backgroundPosition: `${(index * 37) % 100}% ${55 + ((index * 23) % 30)}%` }}>
        <span className="lot-card-cover-text">
          <strong>{lot.name}</strong>
          <small>
            {[lot.crop, lot.areaHa !== null ? `${lot.areaHa.toLocaleString('es-AR')} ha` : null].filter(Boolean).join(' · ')}
          </small>
        </span>
        {tracking && <ProgressRing pct={ph.progressPct} />}
        {lot.access !== 'owner' && <span className="lot-shared-badge">Compartido por {lot.ownerName}</span>}
      </span>

      <span className="lot-card-body">
        {!lot.cycleStart && <span className="lot-card-note">Sin fecha de siembra todavía.</span>}
        {lot.cycleStart && lot.stations.length === 0 && <span className="lot-card-note">Sin estaciones: sumá una para seguir el cultivo.</span>}

        {tracking && (
          <>
            <span className="lot-card-stage">
              <span className="lot-card-stage-name">{lot.stages[ph.stageIndex].name}</span>
              <span className="lot-card-gdd">{formatGdd(ph.accumulated)} °Cd</span>
            </span>
            <span className="lot-progress" aria-hidden="true">
              <span style={{ width: `${ph.progressPct}%` }} />
            </span>
            <span className="lot-card-sub">
              {ph.nextIndex !== null ? (
                <>
                  {lot.stages[ph.nextIndex].name}: faltan {formatGdd(ph.remaining ?? 0)} °Cd
                  {ph.nextDate && ` · ~${shortDate(ph.nextDate)}`}
                </>
              ) : (
                'Ciclo completo'
              )}
            </span>
            {lot.cycleStart && <span className="lot-card-foot">Siembra {formatDate(lot.cycleStart)}</span>}
          </>
        )}
        <span className="lot-card-go">
          Ver lote
          <IonIcon icon={chevronForwardOutline} />
        </span>
      </span>
    </button>
  );
};

// Los lotes del campo con la etapa en que está cada cultivo, calculada con
// los grados día de sus estaciones.
const Lots: React.FC = () => {
  const history = useHistory();
  const { lots, sites, loading, error } = useLots();
  // Escanear el QR de un lote que te compartieron (el mismo escáner que el de estaciones).
  const [showScanner, setShowScanner] = useState(false);
  // Los campos (aunque no tengan lotes) y al final los lotes sin campo.
  const groups = [
    ...sites.map((site) => ({ site, lots: lots.filter((l) => l.siteId === site.id) })),
    ...(lots.some((l) => !l.siteId || !sites.some((s) => s.id === l.siteId))
      ? [{ site: null, lots: lots.filter((l) => !l.siteId || !sites.some((s) => s.id === l.siteId)) }]
      : []),
  ];

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Campo</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => setShowScanner(true)} aria-label="Escanear el QR de un lote compartido">
              <IonIcon icon={qrCodeOutline} />
            </IonButton>
            <IonButton onClick={() => history.push('/app/lots/new')} aria-label="Agregar lote">
              <IonIcon icon={addOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        {loading && (
          <div className="ion-text-center ion-padding">
            <IonSpinner color="primary" />
          </div>
        )}

        {!loading && error && (
          <IonText color="danger">
            <p className="ion-padding">{error}</p>
          </IonText>
        )}

        {!loading && !error && lots.length === 0 && (
          <div className="lots-empty">
            <p>
              <strong>Todavía no cargaste lotes.</strong> Cargá cada lote con su cultivo, su fecha de siembra y las
              estaciones que tiene: la app va a seguir en qué etapa está cada uno con los grados día, sin recorrer el
              campo. Si te compartieron un lote, escaneá su código con el ícono de QR de arriba.
            </p>
            <IonButton onClick={() => history.push('/app/lots/new')}>
              <IonIcon icon={addOutline} slot="start" />
              Cargar el primer lote
            </IonButton>
          </div>
        )}

        {/* Campo → lotes: cada campo con su ubicación y sus lotes debajo. */}
        {groups.map((group) => (
          <section className="campo-group" key={group.site?.id ?? 'none'}>
            <div className="campo-head">
              <span className="campo-head-icon">
                <IonIcon icon={businessOutline} />
              </span>
              <span className="campo-head-text">
                <strong>{group.site ? group.site.name : 'Sin campo'}</strong>
                <small>
                  {group.site
                    ? group.site.lat !== null && group.site.lng !== null
                      ? `Ubicación marcada · ${group.lots.length} lote${group.lots.length === 1 ? '' : 's'}`
                      : 'Sin ubicación: marcala para ver su clima'
                    : 'Lotes que todavía no están en un campo'}
                </small>
              </span>
              {group.site && (
                <button className="campo-edit" onClick={() => history.push('/app/sites')}>
                  <IonIcon icon={createOutline} />
                  Editar
                </button>
              )}
            </div>
            {group.lots.length > 0 ? (
              <div className="lots-grid">
                {group.lots.map((lot, i) => (
                  <LotCard key={lot.id} lot={lot} index={i} onOpen={() => history.push(`/app/lots/${lot.id}`)} />
                ))}
              </div>
            ) : (
              <p className="campo-empty">Todavía no tiene lotes.</p>
            )}
          </section>
        ))}
        <QrScannerModal
          isOpen={showScanner}
          onClose={() => setShowScanner(false)}
          onScan={(text) => {
            setShowScanner(false);
            history.push(joinPathForScan(text));
          }}
        />
      </IonContent>
    </IonPage>
  );
};

export default Lots;
